import { FormEvent } from 'react';
import { NsEmailInput } from 'components/common/ns-email-input/NsEmailInput';
import { Error } from 'components/common/error/Error';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { useMaskPII } from 'hooks/useMaskPII';
import { NsSelect } from 'components/common/ns-select/NsSelect';
import { NsForm } from 'components/common/ns-form/NsForm';
import {
  NsDatepicker,
  DateString
} from 'components/common/ns-datepicker/NsDatepicker';
import { NsFirstNameInput } from 'components/common/ns-first-name-input/NsFirstNameInput';
import { ChangeTenancy } from 'components/direct-sales/create-account/components/ChangeTenancy';
import { NsLastNameInput } from 'components/common/ns-last-name-input/NsLastNameInput';
import { NsPhoneInput } from 'components/common/ns-phone-input/NsPhoneInput';
import { NsAlert } from 'components/common/ns-alert/NsAlert';
import { NsList } from 'components/common/ns-list/NsList';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { PREFERRED_COMMUNICATION_METHOD, PARTNERS } from 'config/app-config';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { NsInputterValidations } from 'components/common/ns-inputter/NsInputterValidations';
import NsInputterEvent from '../../../common/ns-inputter/NsInputterEvent';

export const testId = 'createAccountFormTestId';
export const agentScriptTestId = 'agentScriptTestId';

type CreateAccountFormProps = {
  handleChange: (event: NsInputterEvent) => void;
  handleGoNext: (event: FormEvent<HTMLFormElement>) => void;
  account: {
    title: string;
    firstName: string;
    lastName: string;
    email: string;
    birthDate?: string;
    phoneNumber: string;
    preferredCommunicationMethod?: string;
  };
  apiErrors?: { message: string }[];
  isLoading?: boolean;
  ctaText?: string;
};

export const CreateAccountForm = ({
  handleChange,
  handleGoNext,
  account,
  apiErrors,
  isLoading = false,
  ctaText = 'Continue'
}: CreateAccountFormProps) => {
  const { mask } = useMaskPII();

  const isAgentSalesJourney = useIsLoginEnabledJourney({
    exclude: [PARTNERS.NONDOMESTIC_TRANSFER]
  });

  return (
    <NsForm testId={testId} onSubmit={handleGoNext}>
      <ChangeTenancy />
      <NsSelect
        name="title"
        label="Title"
        onChange={handleChange}
        value={account.title}
        inputAttributes={mask}
        placeholder="Select title"
        options={[
          { value: 'Dr' },
          { value: 'Mr' },
          { value: 'Mrs' },
          { value: 'Ms' },
          { value: 'Miss' }
        ]}
      />

      <NsFirstNameInput
        value={account.firstName}
        onChange={handleChange}
        inputAttributes={mask}
      />

      <NsLastNameInput
        value={account.lastName}
        onChange={handleChange}
        inputAttributes={mask}
      />

      <NsDatepicker
        name="birthDate"
        label="Date of birth"
        value={(account.birthDate as DateString) ?? ('' as DateString)}
        isBirthDate
        onChange={handleChange}
      />

      <NsEmailInput
        id="email"
        name="email"
        label="Email"
        value={account.email}
        onChange={handleChange}
        inputAttributes={mask}
        validations={
          isAgentSalesJourney &&
          account.preferredCommunicationMethod !== 'Email'
            ? [NsInputterValidations.isEmail]
            : [NsInputterValidations.isRequired, NsInputterValidations.isEmail]
        }
      />
      {isAgentSalesJourney && (
        <>
          <NsAlert
            state="info"
            heading="If your customer asks about post"
            testId={agentScriptTestId}
          >
            <p>Most of our customers are paper-free, with these benefits:</p>
            <NsList>
              {[
                <p key={0}>Quicker updates about your energy</p>,
                <p key={1}>Access to your bill online, anytime, anywhere</p>
              ]}
            </NsList>
            <p>If your customer has no email address, choose post.</p>
          </NsAlert>
          <NsRadioInput
            name="preferredCommunicationMethod"
            dataTestId="preferredCommunicationTestId"
            heading="Communications preference"
            onChange={handleChange}
            value={account.preferredCommunicationMethod}
            options={[
              {
                label: 'Email (paper-free)',
                value: PREFERRED_COMMUNICATION_METHOD.EMAIL
              },
              {
                label: 'Post',
                value: PREFERRED_COMMUNICATION_METHOD.POST
              }
            ]}
          />
        </>
      )}

      <NsPhoneInput
        value={account.phoneNumber}
        onChange={handleChange}
        inputAttributes={mask}
      />
      <Error errors={apiErrors ?? []} />
      <NsPrimaryCta isLoading={isLoading}>{ctaText}</NsPrimaryCta>
    </NsForm>
  );
};
